import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";

// Loads Stripe charges from the local Supabase cache on mount (instant), then
// only hits the Stripe API when the user explicitly clicks Refresh. The cache
// is populated by /api/stripe-payments which fetches from Stripe and upserts.
//
// Goal: opening Payments V2 = zero network round-trip to Stripe.
// Goal: Refresh button = re-fetch from Stripe + update cache.

function rowToCharge(r) {
  return {
    id: r.charge_id,
    customerId: r.customer_id || null,
    customerName: r.customer_name || "",
    customerEmail: r.customer_email || "",
    grossAmount: Number(r.gross_amount) || 0,
    refundedAmount: Number(r.refunded_amount) || 0,
    netAmount: r.net_amount != null ? Number(r.net_amount) : null,
    fee: Number(r.fee) || 0,
    currency: r.currency || "USD",
    status: r.status || "succeeded",
    date: r.date || r.created_at_iso?.slice(0, 10) || "",
    description: r.description || "",
    paymentMethodType: r.payment_method_type || null,
    cardLast4: r.card_last4 || null,
    receiptUrl: r.receipt_url || null,
    invoiceId: r.invoice_id || null,
    offerId: r.offer_id || null,
    clientIdOverride: r.client_id_override || null,
  };
}

export function useStripePayments({ defaultSince } = {}) {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [error, setError] = useState(null);
  // Default the date filter way back to capture full Stripe history. Ahmed
  // can still narrow with the Since picker, but the first load + first
  // Refresh both pull everything from this date forward. 2020-01-01 covers
  // the entire UniCamel Stripe account; bump if needed.
  const [since, setSince] = useState(() => {
    if (defaultSince) return defaultSince;
    return "2020-01-01";
  });

  // Load from Supabase cache (instant, no API round trip).
  const loadFromCache = useCallback(async (sinceISO) => {
    setError(null);
    setLoading(true);
    try {
      let query = supabase
        .from("stripe_charges")
        .select("*")
        .order("date", { ascending: false })
        .limit(20000);
      if (sinceISO) query = query.gte("date", sinceISO);
      const { data, error: err } = await query;
      if (err) throw err;
      setCharges((data || []).map(rowToCharge));
      // Most recent updated_at across rows = effective "last synced" stamp.
      const newest = (data || []).reduce((max, r) => (r.updated_at && r.updated_at > max ? r.updated_at : max), "");
      if (newest) setLastSyncedAt(newest);
    } catch (e) {
      setError(e.message || "Could not load cache");
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh = fetch from Stripe (which upserts cache), then re-read cache.
  const refresh = useCallback(async () => {
    setError(null);
    setRefreshing(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const jwt = sessionData?.session?.access_token;
      if (!jwt) throw new Error("Not signed in");
      const res = await fetch(`/api/stripe-payments?since=${since}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text.slice(0, 200));
      }
      const data = await res.json();
      setLastSyncedAt(data.fetchedAt || new Date().toISOString());
      // Re-load from cache so the UI reflects what was just upserted
      await loadFromCache(since);
    } catch (e) {
      setError(e.message || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  }, [since, loadFromCache]);

  // Auto-load cache when since-date changes (or on first mount).
  useEffect(() => { loadFromCache(since); }, [loadFromCache, since]);

  // Supabase realtime: listen for INSERT / UPDATE on stripe_charges. The
  // Stripe webhook (/api/stripe-webhook) upserts new charges into this table
  // the moment Stripe pushes a charge.* event, and Supabase replicates the
  // change to all connected clients. We merge the new row into local state
  // so the Sales tab updates within seconds — no reload, no Refresh click.
  useEffect(() => {
    const channel = supabase
      .channel("stripe_charges_changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "stripe_charges" },
        (payload) => {
          const r = payload.new;
          if (!r) return;
          const incoming = rowToCharge(r);
          setCharges((prev) => {
            const idx = prev.findIndex((c) => c.id === incoming.id);
            if (idx === -1) return [incoming, ...prev];
            const next = prev.slice();
            next[idx] = incoming;
            return next;
          });
          if (r.updated_at) setLastSyncedAt(r.updated_at);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Persist a row-level edit (offer tag, manual client override) to Supabase
  // and update local state optimistically so the UI feels instant.
  const updateCharge = useCallback(async (chargeId, patch) => {
    setCharges((prev) => prev.map((c) => (c.id === chargeId ? { ...c, ...patch } : c)));
    const dbPatch = {};
    if ("offerId" in patch) dbPatch.offer_id = patch.offerId || null;
    if ("clientIdOverride" in patch) dbPatch.client_id_override = patch.clientIdOverride || null;
    if (Object.keys(dbPatch).length === 0) return;
    const { error: err } = await supabase
      .from("stripe_charges")
      .update(dbPatch)
      .eq("charge_id", chargeId);
    if (err) {
      setError(`Save failed: ${err.message}`);
      // Re-load from cache to revert the optimistic change.
      loadFromCache(since);
    }
  }, [since, loadFromCache]);

  return { charges, loading, refreshing, lastSyncedAt, error, since, setSince, refresh, updateCharge };
}
